import { useEffect, useId, useRef, useState } from 'react';
import styles from './InlineTooltip.module.css';

export const TOOLTIP_TEXT = {
  premium: 'The amount you pay to be a member of the plan.',
  deductible:
    'The amount you have to pay for a drug or service before the plan starts to pay.',
  rxDeductible:
    'The amount you have to pay for prescriptions before the plan starts to pay.',
  copay: 'A set cost you pay for a drug or service',
  coinsurance: 'A percentage of the bill you pay for a drug or service',
  oopm: 'The most you can pay (including the deductible), before a plan covers all costs for medical services and drugs in its formulary. It does not include the premium, if there is one, and it might not apply to all services.',
  oopmCurrentYear:
    'The limit for the Medical Out-of-pocket Max, as set by CMS,  is $9,250.',
  excessCharge:
    'Providers can charge up to 15% above the maximum amount Medicare will pay for a service. The member is responsible for paying that charge.',
  rxTiers:
    'Prescription plans have a list of drugs they will cover, called a formulary, which divides drugs into groups, or tiers. Different tiers may have different levels of coverage. A plan’s Summary of Benefits will offer more detail on the plan’s formulary.',
  rxOopm:
    'The most you can pay (including the deductible), before a plan covers all costs for drugs in its formulary. It does not include the premium, if there is one.',
  medicalOopm:
    'The most you can pay (including the deductible), before a plan covers all medical costs. It does not include the premium, if there is one, and it might not apply to all services.',
  extraBenefits:
    'Plans may include extra benefits, such as dental or hearing coverage. These benefits can differ between plans, and plans may not have any wellness benefits at all. Check the plan details to see what wellness extras are offered, if any.',
  inpatientHospital:
    'Care you receive when admitted into the hospital (not just treated in the ER or placed under observation). Inpatient stays can last overnight or longer.',
};

interface InlineTooltipProps {
  text: string;
  tooltipText: keyof typeof TOOLTIP_TEXT;
  isHotspot?: boolean;
  direction?: 'left' | 'right';
}

export default function InlineTooltip({
  text,
  tooltipText,
  isHotspot = false,
  direction = 'right',
}: InlineTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const tooltipId = useId();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node | null;

      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <span className={styles.inlineTooltip}>
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={isOpen}
        aria-describedby={tooltipId}
        aria-haspopup="true"
        aria-label={text}
        onClick={() => setIsOpen((v) => !v)}
      >
        <span className={isHotspot ? styles.hidden : ''}>{text}</span>
      </button>

      {isOpen && (
        <div
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          tabIndex={-1}
          className={`${styles.tooltipText} ${
            direction === 'left' ? styles.left : ''
          }`}
        >
          {TOOLTIP_TEXT[tooltipText]}
        </div>
      )}
    </span>
  );
}
